import { Dialog, DialogPanel } from "@headlessui/react";
import { FunctionComponent, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useCookies } from "react-cookie";

import {
  ModalBackdrop,
  ModalContainer,
  CrossBtn,
  LoginOptionsHeader,
  LoginOptionsBody,
  LoginOptionsSectionDescription,
  OptinPriceSection,
  OptinPriceSubSection,
  OptinPriceLabel,
  OptinPriceValue,
  LoginOptionsDivider,
  LoginOptionsButtonsArea,
  LoginOptionsPrimaryButton,
  LoginOptionsSecondaryButton,
  OptinPanel,
  OptinPriceCard,
  DeclinedConfirmationButtonsArea,
} from "./styles";

import axios from "connection";

import { Status } from "types/Offer";

import Cross from "components/IconImage/cross";

import OrangeWarning from "components/IconImage/orangeWarning";

import { configState, setModal, sendULFEvent, monetaryConfig, setLoader } from "state/config-slice";
// import { selectOfferByName } from "state/offers-slice";
import { ULFEvents } from "types/ULF";
import generateCurrencyValue from "utils/generateCurrencyValue";
import { setNeedsRefetching } from "state/offers-slice";
import { OptInButtonContainer, OptInDoneButton } from "routes/Checkout/styles";
import SuccessBall from "components/IconImage/successBall";
import { setDebugTransactions } from "utils/debugHelper";
import { API_URL, PPE_PATH } from "config";
import { updateDateInString } from "utils/updateDateInString";
import composeEndDate from "utils/composeEndDate";
import getOpco from "utils/getOpco";

const OptinModal: FunctionComponent = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const currentConfig = useSelector(configState);
  const monetarySettings = useSelector(monetaryConfig);
  const modal = currentConfig && currentConfig.modal;
  const [cookies, setCookie] = useCookies();

  const { showModal } = modal;

  const close = () => {
    dispatch(setModal({ showModal: false }));
  };

  const offer = useMemo(
    () => (typeof modal.description === "string" ? JSON.parse(modal.description || "{}") : modal.description ?? {}),
    [modal.description],
  );

  const modalDescription = offer?.marketplaceInfo?.pages[Status.optInRequested]?.optinDetails;
  const endDate = offer?.endDate ? composeEndDate(offer.endDate as string, getOpco()) : "";

  const handleAcceptOptin = useCallback(() => {
    dispatch(
      sendULFEvent({
        page: window.location.pathname,
        "event-name": ULFEvents.OPTIN_ACCEPTED_CLICKED,
        source: cookies.SOURCE,
        isTrackingAllowed: cookies.SUBSCRIPTIONPLUS_TRACKING || false,
      }),
    );

    setCookie("Optin", offer.name, { path: "/" });
    dispatch(setModal({ showModal: false, optinModal: false }));
    navigate("/checkout");
  }, [dispatch, cookies.SOURCE, cookies.SUBSCRIPTIONPLUS_TRACKING, setCookie, offer, navigate]);

  const handleGoBack = useCallback(() => {
    dispatch(setModal({ showModal: false }));
    dispatch(
      sendULFEvent({
        page: window.location.pathname,
        "event-name": ULFEvents.OPTIN_GO_BACK_CLICKED,
        source: cookies.SOURCE,
        isTrackingAllowed: cookies.SUBSCRIPTIONPLUS_TRACKING || false,
      }),
    );
  }, [cookies.SOURCE, cookies.SUBSCRIPTIONPLUS_TRACKING, dispatch]);

  const handleEndSubscription = useCallback(async () => {
    dispatch(setModal({ showModal: false }));
    dispatch(
      sendULFEvent({
        page: window.location.pathname,
        "event-name": ULFEvents.OPTIN_DECLINED_CONFIRMED_CLICKED,
        source: cookies.SOURCE,
        isTrackingAllowed: cookies.SUBSCRIPTIONPLUS_TRACKING || false,
      }),
    );

    const declinedMessage = offer?.marketplaceInfo?.pages[Status.optInRequested]?.optinDetails?.declinedMessage;

    try {
      dispatch(setLoader({ showLoader: true }));

      const result = await axios.put(`${API_URL}${PPE_PATH}/optin`, {
        subscriptionId: offer?.subscriptionId,
        partner: offer?.partner,
        status: "DECLINED",
      });

      dispatch(setNeedsRefetching(true));
      setDebugTransactions(result);
      dispatch(setLoader({ showLoader: false }));
      if (result.data?.optInStatus === Status.optInDeclined) {
        dispatch(
          sendULFEvent({
            page: window.location.pathname,
            "event-name": ULFEvents.OPTIN_DECLINED,
            source: cookies.SOURCE,
            isTrackingAllowed: cookies.SUBSCRIPTIONPLUS_TRACKING || false,
          }),
        );

        dispatch(
          setModal({
            showModal: true,
            errorModal: false,
            optinModal: false,
            title: t("Subscription ending on {{date}}", {
              date: offer?.endDate ? composeEndDate(offer.endDate as string, getOpco()) : "",
            }),
            description: declinedMessage,
            icon: <SuccessBall />,
            cross: true,
            size: "large",
            customButtons: (
              <OptInButtonContainer>
                <OptInDoneButton
                  title={t("Done")}
                  data-testid="optin-declined-done-btn"
                  onClick={() => {
                    dispatch(setNeedsRefetching(true));
                    dispatch(setModal({ showModal: false }));
                    navigate("/offer/" + offer?.name);
                  }}
                />
              </OptInButtonContainer>
            ),
          }),
        );
      }
    } catch (error) {
      dispatch(setLoader({ showLoader: false }));
      dispatch(
        setModal({
          showModal: true,
          errorModal: true,
          optinModal: false,
        }),
      );

      return Promise.reject(error);
    }
  }, [offer, dispatch, cookies.SOURCE, cookies.SUBSCRIPTIONPLUS_TRACKING, t, navigate]);

  const handleDeclineOptin = useCallback(() => {
    dispatch(
      sendULFEvent({
        page: window.location.pathname,
        "event-name": ULFEvents.OPTIN_DECLINED_CLICKED,
        source: cookies.SOURCE,
        isTrackingAllowed: cookies.SUBSCRIPTIONPLUS_TRACKING || false,
      }),
    );

    dispatch(
      setModal({
        showModal: true,
        errorModal: false,
        title: t("Before you cancel"),
        description: updateDateInString(endDate, modalDescription?.declineConfirm ?? ""),
        icon: <OrangeWarning />,
        cross: true,
        size: "large",
        customButtons: (
          <DeclinedConfirmationButtonsArea>
            <LoginOptionsPrimaryButton
              title={t("End my subscription")}
              data-testid="optin-options-email-btn"
              onClick={handleEndSubscription}
            />
            <LoginOptionsSecondaryButton
              title={t("Go back")}
              data-testid="optin-options-msisdn-btn"
              onClick={handleGoBack}
            />
          </DeclinedConfirmationButtonsArea>
        ),
      }),
    );
  }, [
    dispatch,
    cookies.SOURCE,
    cookies.SUBSCRIPTIONPLUS_TRACKING,
    t,
    endDate,
    modalDescription?.declineConfirm,
    handleEndSubscription,
    handleGoBack,
  ]);

  return (
    <Dialog as={ModalContainer} open={showModal} onClose={close} data-testid="optin-modal">
      <ModalBackdrop />

      <DialogPanel as={OptinPanel}>
        <CrossBtn onClick={close} aria-label={t("Close")} title={t("Close")} data-testid="optin-options-close">
          <Cross />
        </CrossBtn>

        <LoginOptionsHeader variant="centerHeader" data-testid="optin-options-header">
          {modal.title}
        </LoginOptionsHeader>

        <LoginOptionsBody>
          <LoginOptionsSectionDescription variant="centerDesciption" data-testid="optin-options-description">
            {updateDateInString(endDate, modalDescription?.shortDescription)}
          </LoginOptionsSectionDescription>

          <OptinPriceSection data-testid="optin-price-section">
            <OptinPriceCard data-testid="optin-price-card-section">
              <OptinPriceSubSection data-testid="optin-current-price">
                <OptinPriceLabel>{t("Current price")}</OptinPriceLabel>
                <OptinPriceValue>
                  {t(modalDescription.isWeekly ? "{{value}}/wk." : "{{value}}/mo.", {
                    value: generateCurrencyValue(modalDescription?.oldPrice, monetarySettings),
                  })}
                </OptinPriceValue>
              </OptinPriceSubSection>
              <LoginOptionsDivider />
              <OptinPriceSubSection data-testid="optin-new-price">
                <OptinPriceLabel>{updateDateInString(endDate, modalDescription?.newPriceDescription)}</OptinPriceLabel>
                <OptinPriceValue>
                  {t(modalDescription.isWeekly ? "{{value}}/wk." : "{{value}}/mo.", {
                    value: generateCurrencyValue(modalDescription?.newPrice, monetarySettings),
                  })}
                </OptinPriceValue>
              </OptinPriceSubSection>
            </OptinPriceCard>
          </OptinPriceSection>
          <LoginOptionsSectionDescription data-testid="optin-options-description2">
            {updateDateInString(endDate, modalDescription?.shortDescription2)}
          </LoginOptionsSectionDescription>
        </LoginOptionsBody>
        <LoginOptionsButtonsArea>
          <LoginOptionsPrimaryButton
            title={t("Proceed with optin")}
            data-testid="optin-options-email-btn"
            onClick={handleAcceptOptin}
          />
          <LoginOptionsSecondaryButton
            title={t("Decline new price")}
            data-testid="optin-options-msisdn-btn"
            onClick={handleDeclineOptin}
          />
        </LoginOptionsButtonsArea>
      </DialogPanel>
    </Dialog>
  );
};

export default OptinModal;
